import { gql } from "lib/apolloClient";
import {
  organizationGeneratedTemplateFragment,
  progressReportTemplateFragment,
  organizationProgressReportTemplateFragment,
  setProgressReportFragment,
  courseProgressReportFragment,
  courseStudentProgressReportFragment,
  studentProgressReportFragment,
  publishedStudentProgressReportFragment,
  organizationProgressReportGroupFragment,
  progressReportObjectiveFeedFragment,
  progressReportTaskGroupFragment,
  progressReportRatingFragment,
  progressReportRemarkFragment,
  studentProgressReportAttendanceFragment,
  aiVoiceTonesFragment,
  progressReportSignatoriesFragment,
  organizationGradesFragment,
  commentTemplatesPaginatedFeedFragment,
  commentTemplatesWithoutPaginationFeedFragment,
  gradingCommentTemplateExcelFragment,
  gpaAssociatedDataFragment,
  gradeScalesReportsFragment,
  learningCourseContextualDataFragment,
  subjectContextualDataFragment,
  plannerElementSetBasicFragment,
  plannerElementSetWithMinimalNodesFragment,
  unitContextualDataFragment,
  atlsContextualDataFragment,
  progressReportPublishedBenchmarkFragment,
  subjectStandardFragment,
  learningCoursesReportsFragment,
  gradingConfigurationFragment,
} from "./ProgressReportFragments";
import {
  studentFragment,
  studentsAlertFragment,
  academicYearFragment,
  staffFragment,
} from "modules/CommonFragments";

export const progressReportGeneratedTemplateQuery = gql`
  query progressReportGeneratedTemplate(
    $organizationId: ID!
    $grades: [ID!]
    $curriculumType: CURRICULUM_PROGRAM_TYPE_ENUM
    $type: PROGRESS_REPORT_TEMPLATE_TYPE_ENUM
    $baseTemplate: JSON
    $gradingPeriodType: PROGRESS_REPORT_GRADING_PERIOD_TYPE_ENUM
    $curriculumProgramId: ID
    $baseTemplateId: String
    $locale: String
    $createdAt: String
    $academicYearId: ID
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationGeneratedTemplateItem
      }
    }
  }
  ${organizationGeneratedTemplateFragment.organizationGeneratedTemplate}
`;

export const getProgressReportTemplateQuery = gql`
  query getProgressReportTemplate($id: ID!, $skipParam: Boolean!) {
    node(id: $id, type: PROGRESS_REPORT_TEMPLATE) {
      id
      ... on ProgressReportTemplate {
        ...progressReportTemplateItem
      }
    }
  }
  ${progressReportTemplateFragment.progressReportTemplateDetails}
`;

export const getAcademicCriteriaSetsForProgressReportTemplateQuery = gql`
  query getAcademicCriteriaSetsForProgressReportTemplate($id: ID!) {
    node(id: $id, type: PROGRESS_REPORT_TEMPLATE) {
      id
      ... on ProgressReportTemplate {
        academicCriteriaSets
      }
    }
  }
`;

export const getProgressReportTemplateFeedQuery = gql`
  query getProgressReportTemplateFeed(
    $organizationId: ID!
    $filters: ProgressReportTemplateFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationProgressReportTemplateItem
      }
    }
  }
  ${organizationProgressReportTemplateFragment.organizationProgressReportTemplate}
`;

export const getProgressReportTemplateFeedCountQuery = gql`
  query getProgressReportTemplateFeedCount(
    $organizationId: ID!
    $filters: ProgressReportTemplateFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationProgressReportTemplateItem
      }
    }
  }
  ${organizationProgressReportTemplateFragment.organizationProgressReportTemplateCount}
`;

export const getProgressReportTemplateAcademicYearFeedQuery = gql`
  query getProgressReportTemplateFeed(
    $organizationId: ID!
    $filters: ProgressReportTemplateFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationProgressReportTemplateItem
      }
    }
  }
  ${organizationProgressReportTemplateFragment.organizationProgressReportTemplateAcademicYearWise}
`;

export const getProgressReportTemplatesQuery = gql`
  query getProgressReportTemplates(
    $id: ID!
    $filters: ProgressReportTemplateFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        progressReportTemplates(filters: $filters) {
          edges {
            node {
              id
              name
              grades {
                id
                name
              }
              gradingPeriodType
              isAttendanceEnabled
            }
          }
        }
      }
    }
  }
`;

//We fetch courses from Organization using node interface
export const getOrganizationCourseFeedV1Query = gql`
  query getOrganizationCourseFeedV1($id: ID!, $filters: CourseFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(filters: $filters) {
          edges {
            node {
              id
              title
              grades {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getSetProgressReportFeedQuery = gql`
  query getSetProgressReportFeed($id: ID!, $searchText: String) {
    node(id: $id, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        ...setProgressReportFragmentItem
      }
    }
  }
  ${setProgressReportFragment.setProgressReport}
`;

export const getOrganizationProgressReportGroupFeedQuery = gql`
  query getOrganizationProgressReportGroupFeed(
    $organizationId: ID!
    $searchText: String
    $academicYearId: ID
    $after: String
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationProgressReportGroupItem
      }
    }
  }
  ${organizationProgressReportGroupFragment.organizationProgressReportGroup}
`;

export const getOrganizationProgressReportGroupsQuery = gql`
  query getOrganizationProgressReportGroups(
    $organizationId: ID!
    $searchText: String
    $academicYearId: ID
    $after: String
    $curriculumProgramIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationProgressReportGroupItem
      }
    }
  }
  ${organizationProgressReportGroupFragment.organizationProgressReportGroupItem}
`;

export const getProgressReportAcademicTermQuery = gql`
  query getProgressReportAcademicTerm($id: ID!) {
    node(id: $id, type: PROGRESS_REPORT_TASK_GROUP) {
      ... on ProgressReportTaskGroup {
        id
        academicTerm {
          id
          label
        }
        academicTerms {
          id
          label
        }
      }
    }
  }
`;

export const downloadProgressReportGroupAsXlsxQuery = gql`
  query downloadProgressReportGroupAsXlsx($taskGroupId: ID!) {
    node(id: $taskGroupId, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        downloadAsXlsx
      }
    }
  }
`;

export const downloadProgressReportGroupAsXlsxV2Query = gql`
  query downloadProgressReportGroupAsXlsxV2($taskGroupId: ID!) {
    node(id: $taskGroupId, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        downloadAsXlsxV2
      }
    }
  }
`;

export const downloadProgressReportGroupAsXlsxV3Query = gql`
  query downloadProgressReportGroupAsXlsxV3($taskGroupId: ID!) {
    node(id: $taskGroupId, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        excelDataDownload(filters: { downloadTypes: [PROOFREADING] }) {
          error
          isSuccess
        }
      }
    }
  }
`;

export const downloadProgressReportGroupAsXlsxCommonQuery = gql`
  query downloadProgressReportGroupAsXlsxCommon(
    $taskGroupId: ID!
    $courseProgressReportTaskIds: [ID!]
    $fileOrganization: [PROGRESS_REPORT_DOWNLOAD_FILE_ORGANIZATION_ENUM!]
    $exportType: PROGRESS_REPORT_DOWNLOAD_DATA_TYPES!
  ) {
    node(id: $taskGroupId, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        excelDataDownload(
          filters: {
            courseProgressReportTaskIds: $courseProgressReportTaskIds
            fileOrganization: $fileOrganization
            downloadTypes: [$exportType]
          }
        ) {
          error
          isSuccess
          url
          filename
        }
      }
    }
  }
`;

export const getCases21XMLQuery = gql`
  query getCases21XML($taskGroupId: ID!) {
    node(type: PROGRESS_REPORT_TASK_GROUP, id: $taskGroupId) {
      ... on ProgressReportTaskGroup {
        cases21XML {
          isSuccess
          url
          filename
          filetype
          error
        }
      }
    }
  }
`;

export const getCourseProgressReportFeedQuery = gql`
  query getCourseProgressReportFeed(
    $courseId: ID!
    $searchText: String!
    $academicTermIds: [ID!]
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseProgressReportItem
      }
    }
  }
  ${courseProgressReportFragment.courseProgressReportFeed}
`;

export const getCourseProgressReportFeedCountQuery = gql`
  query getCourseProgressReportFeedCount($courseId: ID!) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        ...courseProgressReportItem
      }
    }
  }
  ${courseProgressReportFragment.courseProgressReportFeedCount}
`;

export const getCourseStudentProgressReportFeedQuery = gql`
  query getCourseStudentProgressReportFeed(
    $reportId: ID!
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $filters: TaskStudentProgressReportFilter
    $orderByInput: StudentProgressReportOrderByInput
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        id
        studentProgressReport(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          orderByInput: $orderByInput
        ) {
          id
          status
          student {
            id
            firstName
            middleName
            lastName
            profileImage
            isArchived
            isArchivedV2(filters: $userArchivedFilters)
            prefix
          }
        }
      }
    }
  }
`;

export const getCourseStudentProgressReportFeedPaginatedQuery = gql`
  query getCourseStudentProgressReportFeedPaginated(
    $reportId: ID!
    $filters: TaskStudentProgressReportsFilter
    $courseFilters: CourseFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.courseStudentProgressReportFeedPaginated}
`;

export const getStudentProgressReportFeedPaginatedQuery = gql`
  query getStudentProgressReportFeedPaginated(
    $reportId: ID!
    $filters: TaskStudentProgressReportsFilter
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        id
        studentProgressReports(filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              status
              sectionCompleteStatus {
                section
                isCompleted
              }
              student {
                id
                firstName
                middleName
                lastName
                profileImage
                email
                preferredName
                suffix
                pronouns
                type
                isDevUser
                isArchived
                sourceId
                prefix
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
`;

export const getCourseStudentProgressReportFeedsQuery = gql`
  query getCourseStudentProgressReportFeeds(
    $reportIds: [ID!]
    $courseFilters: CourseFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    multiNode(ids: $reportIds, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        ...studentProgressReportItem
      }
    }
  }
  ${studentProgressReportFragment.studentProgressReportFeed}
`;

export const getCourseStudentProgressReportIdsQuery = gql`
  query getCourseStudentProgressReportIds(
    $reportId: ID!
    $filters: TaskStudentProgressReportsFilter
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.courseStudentProgressReportIds}
`;

export const getCourseStudentProgressReportStatusDetailsQuery = gql`
  query getCourseStudentProgressReportStatusDetails(
    $reportId: ID!
    $filters: TaskStudentProgressReportsFilter
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.CourseStudentProgressReportStatusDetails}
`;

export const getCourseProgressReportStudentsDetailQuery = gql`
  query getCourseStudentProgressReportStudentDetails(
    $reportId: ID!
    $filters: TaskStudentProgressReportFilter
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $isSubjectObjectivesSelectedFilter: IsSubjectObjectivesSelectedFilter
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        id
        studentProgressReport(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          id
          student {
            ...studentItem
            alertTypes {
              ...alertTypeFields
            }
            prefix
          }
          completedSections
          status
          isSubjectObjectivesSelected(
            filters: $isSubjectObjectivesSelectedFilter
          )
        }
        progressReportTaskGroup {
          id
          academicTerm {
            id
          }
        }
        parentEntity {
          id
          ... on Grade {
            id
            name
          }
        }
      }
    }
  }
  ${studentFragment.basicStudentDetails}
  ${studentsAlertFragment}
`;

export const getCourseStudentProgressReportHeaderDetailsQuery = gql`
  query getCourseStudentProgressReportHeaderDetails($reportId: ID!) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.courseStudentProgressReportHeaderDetails}
`;

export const getCourseStudentProgressReportBasicDetailsQuery = gql`
  query getCourseStudentProgressReportBasicDetails(
    $reportId: ID!
    $skipParam: Boolean!
  ) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.courseStudentProgressReportBasicDetails}
`;

export const getCourseStudentProgressReportStatusCountQuery = gql`
  query getCourseStudentProgressReportStatusCount($reportId: ID!) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        id
        reportStatusCount {
          status
          count
        }
      }
    }
  }
`;

export const getCourseStudentProgressReportTemplateQuery = gql`
  query getCourseStudentProgressReportTemplate($reportId: ID!) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.courseStudentProgressReportTemplate}
`;

export const getSubjectsDataQuery = gql`
  query getSubjectsData(
    $reportTaskId: ID!
    $param: Boolean!
    $filters: ProgressReportCourseWiseMetricsFilter
    $isCourseBased: Boolean!
    $isSubjectBased: Boolean!
  ) {
    node(id: $reportTaskId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        ...courseStudentProgressReportItem
      }
    }
  }
  ${courseStudentProgressReportFragment.courseStudentProgressReportMetrics}
`;

export const getStudentProgressReportPublishedTemplateQuery = gql`
  query getStudentProgressReportPublishedTemplate(
    $studentProgressReportId: ID!
    $templateFilters: StudentReportTemplateFilter
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        ...studentProgressReportItem
      }
    }
  }
  ${studentProgressReportFragment.studentProgressReportPublishedTemplate}
`;

export const getPublishedStudentProgressReportDetailsQuery = gql`
  query getPublishedStudentProgressReportDetails(
    $studentId: ID!
    $archivalState: ARCHIVAL_STATE_ENUM!
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        academicYears(filters: { archivalState: $archivalState }) {
          ...academicYearItem
        }
        ...publishedStudentProgressReportItem
      }
    }
  }
  ${publishedStudentProgressReportFragment.publishedStudentProgressReport}
  ${academicYearFragment.academicYear}
`;

export const getCourseProgressReportTaskPublishIdsQuery = gql`
  query getCourseProgressReportTaskPublishIds($reportId: ID!) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        allPublishedProgressReport {
          id
        }
      }
    }
  }
`;

export const getStudentProgressReportPublishIdQuery = gql`
  query getStudentProgressReportPublishId($studentProgressReportId: ID!) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        publishedReport {
          id
        }
      }
    }
  }
`;

export const getSubjectGroupWiseReportMetricsQuery = gql`
  query($reportId: ID!, $filters: ProgressReportCourseWiseMetricsFilter) {
    node(id: $reportId, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        id
        otherSectionWiseMetrics(filters: $filters) {
          id
          sections
          pending
          total
          notAssessed
        }
        projectSectionWiseMetrics(filters: $filters) {
          id
          sections
          pending
          total
          notAssessed
        }
        progressReportTaskGroup {
          id
          attendanceMode
        }
        subjectGroupWiseMetrics(filters: $filters) {
          subjectGroup {
            id
            name
            type
          }
          courseWiseStats {
            id
            total
            pending
            completed
            notAssessed
            course {
              id
              title
              subjects {
                id
                name
              }
              profileImageData {
                url
              }
            }
          }
        }
      }
    }
  }
`;

export const getSetProgressReportGradeSubjectObjectivesQuery = gql`
  query getSetProgressReportObjectiveFeed(
    $id: ID!
    $filters: TaskGroupSubjectGradeObjectiveFilter
    $associatedChildPlannerElementFilters: AssociatedChildPlannerElementNodesFilters!
  ) {
    node(id: $id, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        ...progressReportObjectiveFeed
      }
    }
  }
  ${progressReportObjectiveFeedFragment.progressReportObjectiveFeed}
`;

export const getDescriptorForCriterionQuery = gql`
  query getDescriptorForCriterion($input: DescriptorForCriterionInput!) {
    planner {
      getDescriptorForCriterion(input: $input)
    }
  }
`;

export const getStudentsFromGradeQuery = gql`
  query getStudentFromGrade(
    $studentFilters: GradeStudentsFilters
    $id: ID!
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: GRADE) {
      id
      ... on Grade {
        id
        students(filters: $studentFilters) {
          totalCount
          edges {
            node {
              id
              firstName
              middleName
              lastName
              profileImage
              isArchived
              prefix
              isArchivedV2(filters: $userArchivedFilters)
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
      }
    }
  }
  ${studentsAlertFragment}
`;

export const getStaffCoursesQuery = gql`
  query getStaffCourses(
    $id: ID!
    $filters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        coursesV2(filters: $filters) {
          edges {
            node {
              id
              title
              subjects {
                id
              }
              primaryGrade(filters: $courseGradeFilters) {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getCourseProgressReportTaskBasicDetailsQuery = gql`
  query getCourseProgressReportTaskBasicDetails($id: ID!) {
    node(id: $id, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        title
        parentEntity {
          id
          ... on Grade {
            id
            name
          }
        }
      }
    }
  }
`;

export const getProgressReportCourseSectionCompleteStatsQuery = gql`
  query getProgressReportCourseSectionCompleteStats($id: ID!) {
    node(id: $id, type: PROGRESS_REPORT_COURSE_SECTION_COMPLETE_STATS) {
      id
      ... on ProgressReportCourseSectionCompleteStats {
        ...progressReportCourseSectionCompletionStatsItem
      }
    }
  }
  ${courseProgressReportFragment.progressReportCourseSectionCompletionStats}
`;

export const getProgressReportTaskObjectivesQuery = gql`
  query getProgressReportTaskObjectives(
    $id: ID!
    $courseProgressReportTaskFilters: CourseProgressReportTaskFilters
    $studentReportFilters: TaskStudentProgressReportFilter
    $associatedChildPlannerElementFilters: AssociatedChildPlannerElementNodesFilters!
    $mypCriteriaFilters: StudentProgressReportMypCriteriaFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        ...progressReportTaskObjectiveItem
      }
    }
  }
  ${progressReportTaskGroupFragment.progressReportTaskObjective}
`;

export const getProgressReportTaskGroupAcademicTermQuery = gql`
  query getProgressReportTaskGroupAcademicTerm($id: ID!) {
    node(id: $id, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        id
        academicTerms {
          id
          label
          type
          lockingStatus
          associatedAcademicTermEntities {
            id
            academicTerm {
              id
              label
              lockingStatus
            }
          }
        }
        academicTerms {
          id
          label
          type
        }
      }
    }
  }
`;

export const getStudentReportPlannerElementNodesQuery = gql`
  query getStudentReportPlannerElementNodes(
    $studentProgressReportId: ID!
    $associatedChildPlannerElementFilters: AssociatedChildPlannerElementNodesFilters!
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        id
        student {
          id
        }
        courseProgressReportTask {
          id
          parentEntity {
            id
            ... on Grade {
              id
              name
            }
          }
        }
        plannerElementNodes(filters: { elementType: MYP_OBJECTIVES }) {
          id
          label
          associatedParents {
            id
            ... on Subject {
              id
              name
            }
          }
        }
        mypCriteria {
          id
          label
          type
          associatedParents {
            id
            ... on Subject {
              id
              name
            }
          }
        }
        reportSubjects {
          id
          name
          associatedChildPlannerElementNodes(
            filters: $associatedChildPlannerElementFilters
          ) {
            id
            label
          }
        }
      }
    }
  }
`;

export const getCategorySetAssessmentRatingsQuery = gql`
  query getCategorySetAssessmentRatings(
    $academicCriteriaValueId: ID!
    $assessmentRatingFilters: AcademicCriteriaValueAssessmentRatingsFilter
  ) {
    node(id: $academicCriteriaValueId, type: ACADEMIC_CRITERIA_VALUE) {
      id
      ... on AcademicCriteriaValue {
        id
        assessmentRatings(filters: $assessmentRatingFilters) {
          assessment {
            id
            title {
              id
              value
            }
            image {
              id
              value
            }
            assessmentTools(filters: { assessmentToolType: SCORE }) {
              edges {
                node {
                  id
                  assessmentToolItem {
                    ... on ScoreAssessmentTool {
                      id
                      scoreToolData {
                        id
                        maxScore
                      }
                    }
                  }
                  assessmentToolType
                }
              }
            }
          }

          rating {
            id
            value
          }
        }
      }
    }
  }
`;

export const getCourseProgressReportTaskIdUsingStudentProgressReportQuery = gql`
  query getCourseProgressReportTaskIdUsingStudentProgressReport($id: ID!) {
    node(id: $id, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        courseProgressReportTask {
          id
        }
      }
    }
  }
`;

export const getCourseProgressReportTaskWithAcademicYearIdQuery = gql`
  query getCourseProgressReportTaskWithAcademicYearId(
    $id: ID!
    $filters: TermFilters
  ) {
    node(id: $id, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        id
        title
        course {
          id
          title
          isArchived
          grades {
            id
          }
          primaryGrade {
            id
            name
          }
        }
        reportingDates {
          startDate
          endDate
        }
        parentEntity {
          id
          ... on Grade {
            id
            name
          }
          ... on Course {
            id
            title
          }
        }
        progressReportTaskGroup {
          id
          attendanceMode
          studentReflectionDeadline
          academicTerm {
            id
          }
          academicTerms {
            id
            type
          }
          template {
            id
            gradingPeriodType
          }
          academicYear {
            id
            terms(filters: $filters) {
              id
              type
              associatedAcademicTermEntities {
                id
                academicTerm {
                  id
                  type
                }
                cumulativeAcademicTermWeightages {
                  id
                  weightage
                  percentage
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentProgressReportBasicDetailsQuery = gql`
  query getStudentProgressReportBasicDetails(
    $studentProgressReportId: ID!
    $userArchivedFilters: UserArchivedFilters
    $staffStudentRelationFilters: StaffStudentRelationFilter
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        id
        coverSheetBackgroundUrl
        status
        completedSections
        studentReflectionEditInfo {
          isReflectionEditable
          isDeadlinePassed
        }
        studentReflectionFeedback {
          id
          label
          isResolved
          createdBy {
            ...staffItem
          }
        }
        student {
          ...studentItem
          isArchivedV2(filters: $userArchivedFilters)
          gender
          preferredName
          prefix
          address {
            id
            line1
            line2
            city
          }
          alertTypes {
            ...alertTypeFields
          }
          otherDetails {
            id
            dob
            primaryNationality
            birthPlaceLocale
            lastNameLocale
            dobLocale
            religionLocale
            pronouns
            personalEmail
            languages
            secondaryNationality
            firstNameLocale
            primaryNationalityLocale
            seventhAdditionalField
            eighthAdditionalField
          }
          staffRelation(filters: $staffStudentRelationFilters) {
            edges {
              node {
                id
                firstName
                middleName
                lastName
              }
            }
          }
        }
      }
    }
  }
  ${studentFragment.basicStudentDetails}
  ${studentsAlertFragment}
  ${staffFragment.basicStaffDetails}
`;

export const getStudentProgressReportTemplateDetailsQuery = gql`
  query getStudentProgressReportTemplateDetails(
    $studentProgressReportId: ID!
    $templateFilters: StudentReportTemplateFilter
    $skipSignatories: Boolean!
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        id
        template(filters: $templateFilters)
        signatories @skip(if: $skipSignatories) {
          ...progressReportSignatoriesItem
        }
      }
    }
  }
  ${progressReportSignatoriesFragment.progressReportSignatories}
`;

export const getStudentProgressReportContextualDataQuery = gql`
  query getStudentProgressReportContextualData(
    $studentProgressReportId: ID!
    $skipLearningCourses: Boolean!
    $skipUnits: Boolean!
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        id
        contextualData {
          learningCourses {
            ...learningCourseContextualData
          }
          subjects @include(if: $skipLearningCourses) {
            ...subjectContextualData
          }
          units @skip(if: $skipUnits) {
            ...unitContextualData
          }
          atls {
            ...atlContextualData
          }
          gpaAssociatedData {
            ...gpaAssociatedContextualData
            scoreGradeAggregationInclusionMap {
              id
              learningCourse {
                id
              }
              subject {
                id
              }
              grade {
                id
              }
              academicTerm {
                id
              }
              gradingType
              gradeScaleId
            }
          }
        }
      }
    }
  }
  ${learningCourseContextualDataFragment}
  ${subjectContextualDataFragment}
  ${unitContextualDataFragment}
  ${atlsContextualDataFragment}
  ${gpaAssociatedDataFragment.gpaAssociatedContextualData}
`;

export const getStudentProgressReportSubjectsQuery = gql`
  query getStudentProgressReportSubjects(
    $studentProgressReportId: ID!
    $reportSubjectCoursesFilters: SubjectCourseFilters
    $skipLearningCourses: Boolean!
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        id
        reportSubjects @include(if: $skipLearningCourses) {
          id
          name
          courses(filters: $reportSubjectCoursesFilters) {
            edges {
              node {
                id
                title
              }
            }
          }
        }
        reportLearningCourses @skip(if: $skipLearningCourses) {
          id
          displayName
        }
      }
    }
  }
`;

export const getStudentProgressReportDetailsQuery = gql`
  query getStudentProgressReportDetails(
    $studentProgressReportId: ID!
    $remarkFilters: StudentReportRemarkFilter
    $ratingFilters: StudentReportRatingFilter
    $shouldFetchNewEntitiesOnly: Boolean!
    $shouldSkipGpaAssociatedData: Boolean!
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      id
      ... on StudentProgressReport {
        id
        attendanceSummaryV2 {
          ...studentProgressReportAttendanceItemV2
        }
        ratings(filters: $ratingFilters)
          @skip(if: $shouldFetchNewEntitiesOnly) {
          ...progressReportRatingItem
        }
        remarks(filters: $remarkFilters) {
          ...progressReportRemarkItem
        }
        gpaAssociatedData @skip(if: $shouldSkipGpaAssociatedData) {
          ...gpaAssociatedDataItem
        }
        completedSections
        reportProjectNodes
      }
    }
  }
  ${studentProgressReportAttendanceFragment.studentProgressReportAttendanceV2}
  ${progressReportRatingFragment.progressReportRating}
  ${progressReportRemarkFragment.progressReportRemark}
  ${gpaAssociatedDataFragment.gpaAssociatedData}
`;

export const getStudentIbGradeRatingsQuery = gql`
  query getStudentIbGradeRatingsQuery(
    $id: ID!
    $filtersForRatingHistory: ibPYPElementRatingFiltersForRatingHistory
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        ibPYPElementRatings(
          dataSet: RATING_HISTORY
          filtersForRatingHistory: $filtersForRatingHistory
        ) {
          id
          ibPYPElementId
          ibPYPElementType
          value # score
          publishedAt
          parent {
            id
            ... on StudentAssignment {
              id
              assignment {
                id
                content {
                  id
                  ... on Assessment {
                    id
                    title {
                      id
                      value # assessment title
                    }
                    assessmentType {
                      id
                      value
                    }
                    category {
                      id
                      label
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getAIPromptsForReportQuery = gql`
  query getAIPrompts($filters: PromptFilters!) {
    ai {
      prompts(filters: $filters) {
        id
        action
        promptFields {
          field
          type
        }
        metadata
      }
    }
  }
`;

export const getStaffListQuery = gql`
  query getStaffList(
    $filters: OrgConnectionFilterInput
    $organizationId: ID!
    $after: String
    $rnpRoleContextDetailFilter: RnPRoleContextDetailFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: $filters, first: 12, after: $after) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
            __typename
          }
          edges {
            node {
              id
              firstName
              lastName
              role
              prefix
              preferredName
              pronouns
              suffix
              rnpRoleContextDetail(filters: $rnpRoleContextDetailFilter) {
                id
                role {
                  id
                  label
                }
                contextType
                context {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getProgressReportBaseTemplatesQuery = gql`
  query getProgressReportBaseTemplates(
    $organizationId: ID!
    $filters: ProgressReportBaseTemplateFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        progressReportBaseTemplates(filters: $filters) {
          id
          name
          type
        }
      }
    }
  }
`;

export const getGradeListQuery = gql`
  query getGradeList($organizationId: ID!, $GradeFilters: GradeFilterInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $GradeFilters) {
          id
          name
        }
      }
    }
  }
`;

export const getSubjectListQuery = gql`
  query getSubjectList(
    $organizationId: ID!
    $SubjectFilters: SubjectFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        subjects(filters: $SubjectFilters) {
          id
          name
        }
      }
    }
  }
`;

export const getAssociatedCoursesForPYPQuery = gql`
  query getAssociatedCoursesForPYP($id: ID!) {
    node(id: $id, type: COURSE_PROGRESS_REPORT_TASK) {
      id
      ... on CourseProgressReportTask {
        associatedCoursesForPYP {
          id
          title
        }
      }
    }
  }
`;

export const getOrganisationLanguageLocalesQuery = gql`
  query getOrganisationLanguageLocales(
    $organizationId: ID!
    $curriculumIds: [ID]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        curriculumPrograms(curriculumIds: $curriculumIds) {
          id
          programDataLanguages
        }
      }
    }
  }
`;

export const getAIVoiceTonesForCurriculumQuery = gql`
  query getAIVoiceTonesForCurriculum(
    $organizationId: ID!
    $curriculumIds: [ID]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        curriculumPrograms(curriculumIds: $curriculumIds) {
          id
          voiceTones {
            ...progressReportAIVoiceTones
            isGlobalDefault
          }
        }
      }
    }
  }
  ${aiVoiceTonesFragment.progressReportAIVoiceTones}
`;

export const getStaffVoiceToneQuery = gql`
  query getStaffVoiceTone($id: ID!, $filters: StaffAIVoiceToneFilters) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        aiVoiceTone(filters: $filters) {
          ...progressReportAIVoiceTones
        }
      }
    }
  }
  ${aiVoiceTonesFragment.progressReportAIVoiceTones}
`;

export const getAcademicYearTermsQuery = gql`
  query getAcademicYearTerms($academicYearId: ID!, $filters: TermFilters) {
    node(id: $academicYearId, type: ACADEMIC_YEAR) {
      id
      ... on AcademicYear {
        id
        terms(filters: $filters) {
          id
          label
          startDate
          endDate
          type
          associatedAcademicTermEntities {
            id
            academicTerm {
              id
              label
              startDate
              type
            }
            cumulativeAcademicTermWeightages {
              id
              weightage
              percentage
            }
          }
        }
      }
    }
  }
`;

export const getStudentProgressReportRatingHistoryQuery = gql`
  query getStudentProgressReportRatingHistory(
    $studentProgressReportId: ID!
    $studentProgressReportRatingHistoryFilters: StudentProgressReportRatingHistoryFilters
  ) {
    node(id: $studentProgressReportId, type: STUDENT_PROGRESS_REPORT) {
      ... on StudentProgressReport {
        id
        ratingHistory(filters: $studentProgressReportRatingHistoryFilters) {
          id
          ibPYPElementId
          ibPYPElementType
          academicTerm {
            id
          }
          parent {
            id
            ... on StudentAssessmentEvaluation {
              id
              assessment {
                id
                title {
                  id
                  value
                }
                assessmentType {
                  id
                  value
                }
                taskType {
                  id
                  label
                  color
                }
              }
            }
            ... on StudentProgressReport {
              id
              courseProgressReportTask {
                id
                title
              }
            }
            ... on Post {
              id
              title
              attachments {
                id
                type
                title
              }
            }
            ... on ImportedEntity {
              id
              type
              name
            }
            ... on StudentAssignment {
              id
              assignment {
                id
                content {
                  id
                  ... on Assessment {
                    id
                    title {
                      id
                      value
                    }
                    assessmentType {
                      id
                      value
                    }
                    taskType {
                      id
                      label
                      color
                    }
                  }
                }
              }
            }
          }
          parentType
          publishedAt
          subjectId
          academicCriteriaSet {
            id
            label
          }
          academicCriteriaValue {
            id
            label
          }
        }
      }
    }
  }
`;

export const getCurriculumAttendanceOptionsForProgressReportQuery = gql`
  query getCurriculumAttendanceOptionsForProgressReport(
    $id: ID!
    $curriculumId: [ID]
    $filter: AttendanceOptionsSetFilterOptions
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        curriculumPrograms(curriculumIds: $curriculumId) {
          id
          attendanceOptionSet(filter: $filter) {
            id
            attendanceOptions {
              id
              value
            }
            attendanceCategories {
              id
              attendanceOptions {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationMultiCurriculumAttendanceOptionsQuery = gql`
  query getOrganizationMultiCurriculumAttendanceOptions(
    $id: ID!
    $organizationAttendanceFilters: OrganizationAttendanceFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        attendanceV2(filters: $organizationAttendanceFilters) {
          attendanceOptionSet {
            attendanceOptions {
              curriculumProgramSettings {
                settings {
                  value
                }
              }
              attendanceOption {
                id
              }
            }
            attendanceCategories {
              id
              attendanceOptions {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationGradeBasicDetailsQuery = gql`
  query getOrganizationGrades(
    $organizationId: ID!
    $filters: GradeFilterInput
    $gradeStudentsFilters: GradeStudentsFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationGradesItem
      }
    }
  }
  ${organizationGradesFragment.organizationGrades}
`;

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades(
    $gradeIds: [ID!]
    $filters: GradeStudentsFilters
  ) {
    multiNode(ids: $gradeIds, type: GRADE) {
      ... on Grade {
        id
        name
        students(filters: $filters) {
          totalCount
          edges {
            node {
              id
              firstName
              lastName
              profileImage
              registrationCategory
              prefix
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationGradesWithGlobalGradeQuery = gql`
  query getOrganizationGradesWithGlobalGrade(
    $gradeIds: [ID!]
    $filters: GradeStudentsFilters
  ) {
    multiNode(ids: $gradeIds, type: GRADE) {
      ... on Grade {
        id
        name
        globalGrade {
          id
        }
        students(filters: $filters) {
          totalCount
          edges {
            node {
              id
              firstName
              lastName
              profileImage
              registrationCategory
              prefix
              yearGroup {
                id
                grade {
                  id
                  globalGrade {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getGradeEntityProgressReportTaskDetailsQuery = gql`
  query getGradeEntityProgressReportTaskDetails($id: ID!) {
    node(id: $id, type: PROGRESS_REPORT_TASK_GROUP) {
      id
      ... on ProgressReportTaskGroup {
        id
        courseProgressReportTasks {
          id
          studentProgressReport {
            id
            status
            student {
              id
              firstName
              lastName
            }
          }
          parentEntity {
            id
          }
        }
      }
    }
  }
`;

export const getCurriculumGradesAndSubjectsQuery = gql`
  query getCurriculumGradesAndSubjects(
    $id: ID!
    $gradeFilters: GradeFilterInput
    $subjectFilters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $gradeFilters) {
          id
          name
        }
        subjects(filters: $subjectFilters) {
          id
          name
        }
      }
    }
  }
`;

export const getGradingCommentTemplatesQuery = gql`
  query getGradingCommentTemplates(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: GradingCommentTemplateFilterOptions
    $paginated: Boolean!
  ) {
    node(id: $id, type: $type) {
      ... on IbPypCurriculumProgram {
        id
        gradingCommentTemplates(filters: $filters) {
          ...commentTemplatesPaginatedFeed @include(if: $paginated)
          ...commentTemplatesWithoutPaginatedFeed @skip(if: $paginated)
        }
        gradingCommentTemplateTags {
          ...commentTemplatesTags
        }
      }

      ... on IbMypCurriculumProgram {
        id
        gradingCommentTemplates(filters: $filters) {
          ...commentTemplatesPaginatedFeed @include(if: $paginated)
          ...commentTemplatesWithoutPaginatedFeed @skip(if: $paginated)
        }
        gradingCommentTemplateTags {
          ...commentTemplatesTags
        }
      }

      ... on UBDCurriculumProgram {
        id
        gradingCommentTemplates(filters: $filters) {
          ...commentTemplatesPaginatedFeed @include(if: $paginated)
          ...commentTemplatesWithoutPaginatedFeed @skip(if: $paginated)
        }
        gradingCommentTemplateTags {
          ...commentTemplatesTags
        }
      }

      ... on IbDpCurriculumProgram {
        id
        gradingCommentTemplates(filters: $filters) {
          ...commentTemplatesPaginatedFeed @include(if: $paginated)
          ...commentTemplatesWithoutPaginatedFeed @skip(if: $paginated)
        }
        gradingCommentTemplateTags {
          ...commentTemplatesTags
        }
      }
    }
  }
  ${commentTemplatesPaginatedFeedFragment.commentTemplatesPaginatedFeed}
  ${commentTemplatesWithoutPaginationFeedFragment.commentTemplatesWithoutPaginatedFeed}
  ${commentTemplatesWithoutPaginationFeedFragment.commentTemplatesTags}
`;

export const downloadGradingCommentTemplateAsExcelQuery = gql`
  query downloadGradingCommentTemplateAsExcel(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: GradingCommentTemplateExcelDownloadFilterOptions!
  ) {
    node(id: $id, type: $type) {
      ... on IbPypCurriculumProgram {
        id
        gradingCommentTemplateExcelDownload(filters: $filters) {
          ...gradingCommentTemplateExcel
        }
      }
      ... on IbMypCurriculumProgram {
        id
        gradingCommentTemplateExcelDownload(filters: $filters) {
          ...gradingCommentTemplateExcel
        }
      }
      ... on IbDpCurriculumProgram {
        id
        gradingCommentTemplateExcelDownload(filters: $filters) {
          ...gradingCommentTemplateExcel
        }
      }
      ... on UBDCurriculumProgram {
        id
        gradingCommentTemplateExcelDownload(filters: $filters) {
          ...gradingCommentTemplateExcel
        }
      }
    }
  }
  ${gradingCommentTemplateExcelFragment.gradingCommentTemplateExcel}
`;

export const getOrganizationAcademicYearsForGradingHistoryQuery = gql`
  query getOrganizationAcademicYearsForGradingHistory(
    $id: ID!
    $termFilters: TermFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        academicYears {
          id
          startDate
          endDate
          terms(filters: $termFilters) {
            id
            label
            abbreviation
            type
            lockingStatus
            associatedAcademicTermEntities {
              id
              academicTerm {
                id
                type
              }
              cumulativeAcademicTermWeightages {
                id
                weightage
                percentage
              }
            }
          }
        }
      }
    }
  }
`;

export const getGradingCommentTypesQuery = gql`
  query getGradingCommentTypes {
    documentation {
      gradingCommentTypes {
        id
        type
      }
    }
  }
`;

export const getOrganizationAcademicCoursesQuery = gql`
  query getOrganizationAcademicCourses(
    $id: ID!
    $academicCourseFilters: AcademicCourseFilters
    $orderBy: ACADEMIC_COURSE_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        academicCourses(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $academicCourseFilters
        ) {
          id
          label
          code
          isArchived
        }
      }
    }
  }
`;

export const getCurriculumWiseGradeScalesQuery = gql`
  query getCurriculumWiseGradeScales(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
    $filters: GradeScaleFilters
    $gradeScaleEntityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: $type) {
      ... on UBDCurriculumProgram {
        id
        gradeScale(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          ...gradeScalesReportsFragment
        }
      }
      ... on IbMypCurriculumProgram {
        id
        gradeScale(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          ...gradeScalesReportsFragment
        }
      }
      ... on IbDpCurriculumProgram {
        id
        gradeScale(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          ...gradeScalesReportsFragment
        }
      }
      ... on IbPypCurriculumProgram {
        id
        gradeScale(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          ...gradeScalesReportsFragment
        }
      }
    }
  }
  ${gradeScalesReportsFragment.gradeScalesReportsFragment}
`;

export const getOrganizationSubjectsAssociationsQuery = gql`
  query getOrganizationSubjectsAssociations(
    $id: ID!
    $filters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $filters) {
          id
          name
          isArchived
        }
      }
    }
  }
`;

export const getStandardSetsQuery = gql`
  query getStandardSets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: PlannerElementNodeSetFilters
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: $type) {
      id
      ... on UBDCurriculumProgram {
        plannerElementNodeSets(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              title
              subject {
                id
              }
            }
          }
        }
      }

      ... on IbMypCurriculumProgram {
        plannerElementNodeSets(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              title
              subject {
                id
              }
            }
          }
        }
      }

      ... on IbPypCurriculumProgram {
        plannerElementNodeSets(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              title
              subject {
                id
              }
            }
          }
        }
      }

      ... on IbDpCurriculumProgram {
        plannerElementNodeSets(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              title
              subject {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getPublishedStandardsQuery = gql`
  query getPublishedStandards(
    $id: ID!
    $isPYPCurriculum: Boolean! = false
    $filters: PlannerElementNodeFilters
  ) {
    node(id: $id, type: PLANNER_ELEMENT_NODE_SET) {
      ... on PlannerElementNodeSet {
        id
        standardSetWrapper {
          publishedStandards(filters: $filters) @skip(if: $isPYPCurriculum) {
            edges {
              node {
                id
                uid
                label
                subText
                gradingType
                code
                depth
                parentData {
                  id
                }
                standardsWrapper {
                  properties {
                    isArchived
                    isPowerStandard
                    grades {
                      id
                      name
                    }
                    tags {
                      id
                      label
                    }
                  }
                }
              }
            }
          }
          publishedBenchmarks(filters: $filters)
            @include(if: $isPYPCurriculum) {
            ...progressReportPublishedBenchmarkItem
          }
        }
      }
    }
  }
  ${progressReportPublishedBenchmarkFragment.progressReportPublishedBenchmark}
`;

export const getCurriculumStandardSetsQuery = gql`
  query getCurriculumStandardSets(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $subjectIds: [ID!]
    $setId: ID!
    $filters: PlannerElementNodeFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        plannerElementNodeSets(
          filters: {
            status: [PUBLISHED, ARCHIVED, PARTIALLY_PUBLISHED]
            subjects: $subjectIds
            plannerElementIds: [$setId]
          }
        ) {
          edges {
            node {
              id
              title
              subject {
                id
                name
                isArchived
              }
              isArchived
              standardSetWrapper {
                publishedBenchmarks(filters: $filters) {
                  ...progressReportPublishedBenchmarkItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${progressReportPublishedBenchmarkFragment.progressReportPublishedBenchmark}
`;

export const getPlannerElementSetTitleQuery = gql`
  query getPlannerElementSetTitle(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $elementTypes: [PLANNER_ELEMENT_TYPE_ENUM]
    $pypElementTypes: [String!]
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $pypElementTypes) {
          ...genericPlannerElementSetBasicItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetBasicItem
        }
      }
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetBasicItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetBasicItem
        }
      }
    }
  }
  ${plannerElementSetBasicFragment.plannerElementSetBasic}
  ${plannerElementSetBasicFragment.genericPlannerElementSetBasic}
`;

export const getPlannerElementNodesForReportsQuery = gql`
  query getPlannerElementNodesForReports(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $elementTypes: [PLANNER_ELEMENT_TYPE_ENUM]
    $pypElementTypes: [String!]
    $filters: PlannerElementFilters
    $atlFilters: ATLFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $pypElementTypes) {
          ...plannerElementSetWithNodesItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithMinimalNodesItem
        }
      }
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithMinimalNodesItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          ...plannerElementSetWithMinimalNodesItem
        }
      }
    }
  }
  ${plannerElementSetWithMinimalNodesFragment.plannerElementSetWithMinimalNodes}
  ${plannerElementSetWithMinimalNodesFragment.plannerElementSetWithNodes}
`;

export const getPlannerElementNodesOfSetForReportsQuery = gql`
  query getPlannerElementNodesOfSet($id: ID!, $filters: PlannerElementFilters) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        ...plannerElementSetWithMinimalNodesItem
      }
    }
  }
  ${plannerElementSetWithMinimalNodesFragment.plannerElementSetWithMinimalNodes}
`;

export const getTemplateRemarksQuery = gql`
  query getTemplateRemarks($id: ID!, $input: ReportTemplateRemarksInput!) {
    node(id: $id, type: PROGRESS_REPORT_TEMPLATE) {
      id
      ... on ProgressReportTemplate {
        id
        templateRemarks(input: $input) {
          id
          nodeId
        }
      }
    }
  }
`;

export const getAssessmentStandardSetsQuery = gql`
  query getStandardSets(
    $id: ID!
    $filters: PlannerElementNodeSetFilters
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          ...subjectStandardItem
        }
      }
    }
  }
  ${subjectStandardFragment}
`;

export const getLearningGoalsGradingDataQuery = gql`
  query getLearningGoalsGradingData(
    $id: ID!
    $plannerElementNodeSetsFilters: PlannerElementNodeSetFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetsFilters) {
          edges {
            node {
              id
              title
              type
              gradeScales {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getCurriculumWiseLearningCoursesQuery = gql`
  query getCurriculumWiseLearningCourses(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $filters: LearningCourseFilters
  ) {
    node(id: $id, type: $type) {
      ... on UBDCurriculumProgram {
        id
        learningCourse(
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          ...learningCoursesReportsFragment
        }
      }
    }
  }
  ${learningCoursesReportsFragment.learningCoursesReportsFragment}
`;

export const getAggregateGradeScalesConfigQuery = gql`
  query getAggregateGradeScalesConfigQuery(
    $id: ID!
    $gradingConfigFilters: GradingConfigurationFilters
    $settingsFilters: GradingConfigurationSettingFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        gradingConfiguration(filters: $gradingConfigFilters) {
          edges {
            node {
              id
              ...GradingConfigurationFragment
            }
          }
        }
        scoreGradeAggregationConfigs {
          ...ScoreGradeAggregationFragment
        }
      }
    }
  }
  ${gradingConfigurationFragment.gradingConfigurationFragment}
  ${gradingConfigurationFragment.scoreGradeAggregationFragment}
`;
